import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function Partners() {
    const { t, lang } = useLang();
    const [partners, setPartners] = useState(null);

    useEffect(() => {
        axios.get(`${API}/partners`).then((r) => setPartners(r.data)).catch(() => setPartners([]));
    }, []);

    if (!partners || partners.length === 0) return null;

    const descFor = (p) =>
        p.description?.[lang.toLowerCase()] || p.description?.en || "";

    return (
        <section id="partners" data-testid="partners-section" className="py-24 md:py-32 bg-ink">
            <div className="max-w-7xl mx-auto px-6 md:px-12 mb-14">
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.partners.label}</span>
                <h2 className="font-graffiti text-4xl md:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                    {t.partners.title}
                </h2>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="border-t border-white/10">
                    {partners.map((p, i) => {
                        const desc = descFor(p);
                        const linkProps = p.website_url
                            ? { href: p.website_url, target: "_blank", rel: "noreferrer" }
                            : {};
                        return (
                            <motion.div
                                key={p.id}
                                data-testid={`partner-row-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: Math.min(i * 0.06, 0.3) }}
                                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center py-10 md:py-12 border-b border-white/10 group"
                            >
                                {/* Logo / name — clickable */}
                                <div className="md:col-span-4 lg:col-span-3">
                                    {p.website_url ? (
                                        <a {...linkProps} data-testid={`partner-link-${i}`} className="block w-fit">
                                            <PartnerBrand p={p} />
                                        </a>
                                    ) : (
                                        <PartnerBrand p={p} />
                                    )}
                                </div>

                                {/* Description */}
                                <div className="md:col-span-8 lg:col-span-9">
                                    {desc ? (
                                        <p data-testid={`partner-desc-${i}`} className="text-zinc-300 text-base md:text-lg leading-relaxed whitespace-pre-line max-w-3xl">
                                            {desc}
                                        </p>
                                    ) : (
                                        <p className="text-zinc-600 text-sm italic">—</p>
                                    )}
                                    {p.website_url && (
                                        <a
                                            {...linkProps}
                                            data-testid={`partner-visit-${i}`}
                                            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-zinc-400 hover:text-volt transition-colors"
                                        >
                                            {p.name} <ArrowUpRight size={14} />
                                        </a>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-10">
                    <a href="#contact" data-testid="partners-cta" className="btn-ghost inline-flex items-center gap-2 px-6 py-3 text-xs uppercase font-bold tracking-wider">
                        {t.partners.cta} →
                    </a>
                </div>
            </div>
        </section>
    );
}

const PartnerBrand = ({ p }) => (
    <div className="flex items-center gap-4">
        {p.logo_url ? (
            <div className="shrink-0 bg-white p-2.5 md:p-3 border border-white/10 opacity-90 group-hover:opacity-100 transition-opacity">
                <img
                    src={resolveUrl(p.logo_url)}
                    alt={p.name}
                    className="h-10 md:h-12 max-w-[140px] object-contain"
                />
            </div>
        ) : null}
        <span className="font-graffiti text-2xl md:text-3xl text-white leading-none group-hover:text-volt transition-colors">
            {p.name}
        </span>
    </div>
);
